import React, {useState, useRef, useCallback, useEffect} from 'react'

import * as Comlink from 'comlink'
import {getMockupWorker} from '../../webworkers/helpers/customShape'

// const EXPORT_DIR = 'Mockup-Custom_Shape_2-side_Printed_Wood_Bookshelf_Sign_1772532498'
const EXPORT_DIR = 'Mockup-Custom_Shape_2-side_Printed_Clear_Acrylic_Tag_1772532538'
const ASSET_BASE_URL = `http://localhost:9001/${EXPORT_DIR}`

export default function MockupTestPage() {
    /**
     * States
     */
    const [status, setStatus] = useState('Waiting...')
    const [logs, setLogs] = useState([])
    const [initialized, setInitialized] = useState(false)

    /**
     * Refs
     */
    const workerApiRef = useRef(null)
    const canvasRef = useRef(null)
    const containerRef = useRef(null)
    const sizeRef = useRef({width: 0, height: 0})

    const log = useCallback((msg) => {
        const time = new Date().toLocaleTimeString()
        setLogs((prev) => [...prev, `[${time}] ${msg}`].slice(-50))
        console.log(`[MockupTest] ${msg}`)
    }, [])

    const initWorker = async () => {
        try {
            log('Fetching worker instance...')
            const api = await getMockupWorker()
            workerApiRef.current = api

            const canvas = canvasRef.current
            if (!canvas) throw new Error('Canvas element not found')

            const {width: w, height: h} = containerRef.current?.getBoundingClientRect() ?? {width: 900, height: 700}
            const width = Math.round(w) || 900
            const height = Math.round(h) || 700

            sizeRef.current = {width, height}
            canvas.width = width
            canvas.height = height

            const offscreen = canvas.transferControlToOffscreen()

            log(`Initializing Renderer inside Worker at ${width}x${height}...`)
            const success = await api.init(Comlink.transfer(offscreen, [offscreen]), width, height)

            if (success) {
                setStatus('Worker Ready ✓')
                setInitialized(true)
                log('Renderer initialized successfully')
            } else {
                throw new Error('Worker init returned false')
            }
        } catch (err) {
            log(`Init Error: ${err.message}`)
            setStatus('Error Initializing')
        }
    }

    const loadAndRender = useCallback(async () => {
        if (!workerApiRef.current) {
            alert('Please init worker first')
            return
        }
        try {
            setStatus('Fetching data...')
            const resp = await fetch(`${ASSET_BASE_URL}/document.json`)
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
            const documentJson = await resp.json()

            setStatus('Loading resources...')
            const {width, height} = sizeRef.current
            log(`Loading resources and rendering at ${width}x${height}...`)

            const start = performance.now()

            await workerApiRef.current.loadResources({
                documentJson,
                replacements: {},
                baseUrl: ASSET_BASE_URL,
            })

            const elapsed = (performance.now() - start).toFixed(0)
            log(`Render complete: ${elapsed}ms`)
            setStatus(`Done ✓ (${elapsed}ms)`)
        } catch (err) {
            log(`Render Error: ${err.message}`)
            setStatus('Failed')
        }
    }, [log])


    const exportBitmap = useCallback(async () => {
        if (!workerApiRef.current) return
        try {
            setStatus('Exporting...')
            const start = performance.now()
            const bitmap = await workerApiRef.current.getBitmap(true)
            const elapsed = (performance.now() - start).toFixed(0)
            if (bitmap instanceof ImageBitmap) {
                const tmp = new OffscreenCanvas(bitmap.width, bitmap.height)
                const ctx = tmp.getContext('bitmaprenderer')
                ctx.transferFromImageBitmap(bitmap)
                const blob = await tmp.convertToBlob({type: 'image/png'})
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = 'mockup_export.png'
                a.click()
                URL.revokeObjectURL(url)
                log(`Export complete: ${elapsed}ms`)
                setStatus(`Exported ✓ (${elapsed}ms)`)
            }
        } catch (err) {
            log(`Export Error: ${err.message}`)
            setStatus('Export Failed')
        }
    }, [log])

    const testUpdate = useCallback(async () => {
        if (!workerApiRef.current) return
        try {
            const layerId = prompt('Nhập ID của Layer muốn test update (vd: thay đổi opacity xuống 50%):')
            if (!layerId) return

            setStatus('Updating layer...')
            const start = performance.now()
            await workerApiRef.current.update({
                [layerId]: { opacity: 128 }
            })
            const elapsed = (performance.now() - start).toFixed(0)
            
            log(`Update complete: ${elapsed}ms`)
            setStatus(`Updated ✓ (${elapsed}ms)`)
        } catch (err) {
            log(`Update Error: ${err.message}`)
            setStatus('Update Failed')
        }
    }, [log])

    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        let timeoutId = null
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0]
            if (!entry || !initialized || !workerApiRef.current) return
            const {width, height} = entry.contentRect
            if (width && height) {
                const w = Math.round(width)
                const h = Math.round(height)
                sizeRef.current = {width: w, height: h}
                clearTimeout(timeoutId)
                timeoutId = setTimeout(() => {
                    workerApiRef.current?.resize({width: w, height: h})
                }, 150)
            }
        })

        observer.observe(container)
        return () => {
            observer.disconnect()
            clearTimeout(timeoutId)
        }
    }, [initialized])

    return (
        <div
            style={{
                padding: '20px',
                fontFamily: 'sans-serif',
                backgroundColor: '#f0f2f5',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <h1 style={{fontSize: '24px', marginBottom: '20px'}}>PixiJS v8 Mockup Engine — OffscreenCanvas Mode</h1>
            <div style={{display: 'flex', gap: '20px', flexGrow: 1}}>
                <div
                    style={{
                        minWidth: '260px',
                        background: '#fff',
                        padding: '20px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                    }}
                >
                    <button onClick={initWorker} disabled={initialized} style={btnStyle('#007bff')}>
                        1. Khởi tạo Engine
                    </button>
                    <button onClick={loadAndRender} disabled={!initialized} style={btnStyle('#28a745')}>
                        2. Load & Render
                    </button>
                    <button onClick={exportBitmap} disabled={!initialized} style={btnStyle('#fd7e14')}>
                        3. Export PNG
                    </button>
                    <button onClick={testUpdate} disabled={!initialized} style={btnStyle('#6f42c1')}>
                        4. Test Update Layer
                    </button>

                    <div style={{marginTop: '8px', padding: '10px', backgroundColor: '#e9ecef', borderRadius: '6px'}}>
                        <div style={{fontSize: '12px'}}>
                            <strong>Status:</strong> {status}
                        </div>
                        <div style={{fontSize: '11px', color: '#666', marginTop: '4px'}}>
                            {initialized
                                ? '✅ OffscreenCanvas active — render trực tiếp từ Worker'
                                : '⬜ Chưa khởi tạo'}
                        </div>
                    </div>

                    <div
                        style={{
                            marginTop: '8px',
                            background: '#1e1e1e',
                            color: '#7cfc00',
                            padding: '10px',
                            flexGrow: 1,
                            minHeight: '200px',
                            overflowY: 'auto',
                            fontSize: '11px',
                            fontFamily: 'Consolas, monospace',
                            borderRadius: '6px',
                        }}
                    >
                        {logs.map((l, i) => (
                            <div key={i} style={{marginBottom: '4px'}}>
                                {l}
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Canvas preview ── */}
                <div
                    ref={containerRef}
                    style={{
                        flexGrow: 1,
                        background: '#dbdbdb',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: 'inset 0 0 10px rgba(0,0,0,0.2)',
                        minHeight: '700px',
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <canvas
                        ref={canvasRef}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                        }}
                    />
                    {!initialized && (
                        <span style={{color: '#999', fontSize: '14px'}}>
                            Nhấn "Khởi tạo Engine" để bắt đầu
                        </span>
                    )}
                </div>
            </div>
        </div>
    )
}

const btnStyle = (bg) => ({
    padding: '10px 16px',
    background: bg,
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'block',
    width: '100%',
    fontWeight: 'bold',
    fontSize: '14px',
    opacity: 1,
    transition: 'opacity 0.2s',
})
